import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import './StrategyNameEditor.css';

/**
 * A strategy's display name with an inline Rename. Only the name members see
 * changes: the slug (its URL and webhook address) is fixed and shown as the ID.
 * Server: PUT /api/admin/strategies/:slug/name.
 */
export default function StrategyNameEditor({ slug, name, onRenamed, showSlug = true, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (!editing) setDraft(name || ''); }, [name, editing]);

  const cancel = () => { setEditing(false); setError(''); setDraft(name || ''); };

  const save = async (e) => {
    e.preventDefault();
    const next = draft.trim();
    if (!next) { setError('Name is required.'); return; }
    if (next === name) { cancel(); return; }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.adminBrandStrategyName(slug), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({ displayName: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t rename the strategy');
      setEditing(false);
      if (onRenamed) onRenamed(data.displayName || next);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`sne${className ? ` ${className}` : ''}`}>
      {editing ? (
        <form className="sne-form" onSubmit={save}>
          <input
            className="sne-input"
            value={draft}
            maxLength={120}
            autoFocus
            aria-label="Strategy name"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') cancel(); }}
            disabled={busy}
          />
          <button type="submit" className="sne-btn sne-btn-primary" disabled={busy || !draft.trim()}>{busy ? 'Saving…' : 'Save'}</button>
          <button type="button" className="sne-btn" onClick={cancel} disabled={busy}>Cancel</button>
        </form>
      ) : (
        <div className="sne-view">
          <strong className="sne-name">{name}</strong>
          <button type="button" className="sne-rename" onClick={() => setEditing(true)} title="Change the name members see">Rename</button>
        </div>
      )}
      {showSlug && slug ? <span className="sne-slug" title="Fixed ID: the strategy's URL and webhook address use it">ID: {slug}</span> : null}
      {error ? <span className="sne-error" role="alert">{error}</span> : null}
    </div>
  );
}
